import { SelectionModel } from '@angular/cdk/collections';
import { CdkCellDef, CdkFooterCellDef, CdkHeaderCellDef } from '@angular/cdk/table';
import {
    AfterViewInit,
    Component,
    ContentChildren,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    QueryList,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';
import { MatSort } from '@angular/material/sort';
import { MatColumnDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AddDataSourceGeneric } from './datasource-generic';
import { SelectionService } from './selection.service';

@Component({
    selector: 'add-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
})
export class TableComponent<Titem, Tkey> implements AfterViewInit, OnInit, OnDestroy {
    @Input() dataSource: AddDataSourceGeneric<Titem>;
    @Input() keySelector: (item: Titem) => Tkey;
    @Input() isReadSelector: (item: Titem) => boolean;
    @Input() isArchivedSelector: (item: Titem) => boolean;
    @Input() selection: SelectionModel<Tkey>;
    @Input() pageSizeOptions = [10, 25, 50, 100];
    @Input() showPaginator: boolean;

    @Output() rowClick = new EventEmitter<{ event: MatSelectChange; row: Titem }>();
    @Output() selectionChange = new EventEmitter<Titem | Titem[]>();

    @ViewChild('table', { static: true }) table: MatTable<Titem>;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ContentChildren(MatColumnDef) columnDefinitions: QueryList<MatColumnDef>;
    displayedColumns: string[] = [];

    skeletonDataSource: MatTableDataSource<Titem>;
    @ViewChild('skeletonTable', { static: true }) skeletonTable: MatTable<Titem>;
    @ViewChild('skeletonTemplateRef') public skeletonTemplateRef: TemplateRef<any>;
    @ViewChild('skeletonHeaderTemplateRef') public skeletonHeaderTemplateRef: TemplateRef<any>;
    @ViewChild('skeletonFooterTemplateRef') public skeletonFooterTemplateRef: TemplateRef<any>;
    skeletonDisplayedColumns: string[] = [];

    selectionService: SelectionService<Titem, Tkey>;

    private isShiftKeyPressed = false;
    private destroy$ = new Subject();

    constructor(@Optional() private matSort: MatSort) {
        this.selectionService = new SelectionService<Titem, Tkey>();
        this.showPaginator = true;
    }

    ngOnInit(): void {
        this.displayedColumns = [];
        this.skeletonDataSource = new MatTableDataSource([<Titem>{}, <Titem>{}, <Titem>{}, <Titem>{}, <Titem>{}]);
        this.skeletonDataSource.sort = null;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    @HostListener('mousedown', ['$event'])
    onMouseDown(event: PointerEvent) {
        if (event.shiftKey) {
            event.preventDefault();
        }
        this.isShiftKeyPressed = event.shiftKey;
    }

    ngAfterViewInit() {
        // must wait to initialize columns until after view init: need the 'ViewChild' template refs.
        if (this.selection) {
            this.displayedColumns.push('isSelected');
        }

        this.columnDefinitions.map((cd) => {
            // add column to the data table
            this.displayedColumns.push(cd.name);
            this.table.addColumnDef(cd);

            // add column to the skeleton
            const skeletonCd = new MatColumnDef();
            skeletonCd.cell = new CdkCellDef(this.skeletonTemplateRef);
            skeletonCd.name = cd.name;
            skeletonCd.sticky = cd.sticky;
            skeletonCd.stickyEnd = cd.stickyEnd;
            skeletonCd.cssClassFriendlyName = cd.cssClassFriendlyName;
            skeletonCd.headerCell = new CdkHeaderCellDef(this.skeletonHeaderTemplateRef);
            skeletonCd.footerCell = new CdkFooterCellDef(this.skeletonFooterTemplateRef);
            this.skeletonDisplayedColumns.push(skeletonCd.name);
            this.skeletonTable.addColumnDef(skeletonCd);
        });

        this.dataSource.initialize(this.matSort, this.paginator);
        this.dataSource
            .connect()
            .pipe(takeUntil(this.destroy$))
            .subscribe((data) => {
                if (data) {
                    this.selectionService.checkSelectionOfCheckbox(this.dataSource, this.selection, this.keySelector);
                }
            });
    }

    onRowClick(event: MatSelectChange, row: Titem) {
        this.selectionService.updateSelection(row, this.isShiftKeyPressed, this.dataSource, this.selection, this.keySelector);
        this.rowClick.emit({ event, row });
        this.selectionChange.emit(row);
    }

    onSelectionClicked($event) {
        $event.stopPropagation();
    }

    onSelectionChanged(row: Titem) {
        this.selectionService.updateSelection(row, this.isShiftKeyPressed, this.dataSource, this.selection, this.keySelector);
        this.selectionChange.emit(row);
    }

    toggleSelectAll() {
        this.selectionService.toggleSelectAll(this.dataSource, this.selection, this.keySelector);
        const selectionList = this.selectionService.isAllSelected ? this.dataSource.getLoadedData() : [];
        this.selectionChange.emit(selectionList);
    }
}

// https://stackoverflow.com/questions/47593692/how-to-translate-mat-paginator-in-angular-4
export function getPaginatorIntl() {
    const paginatorIntl = new MatPaginatorIntl();

    paginatorIntl.itemsPerPageLabel = $localize`:@@ItemsPerPage:Items per page`;
    paginatorIntl.nextPageLabel = $localize`:@@NextPage:Next page`;
    paginatorIntl.previousPageLabel = $localize`:@@PreviousPage:Previous page`;
    paginatorIntl.lastPageLabel = $localize`:@@LastPage:Last page`;
    paginatorIntl.firstPageLabel = $localize`:@@FirstPage:First page`;
    paginatorIntl.getRangeLabel = (page: number, pageSize: number, length: number) => {
        if (length == 0 || pageSize == 0) {
            return '0 ' + +$localize`:@@of:of` + ' ' + length;
        }

        length = Math.max(length, 0);
        const startIndex = page * pageSize;
        const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
        return startIndex + 1 + ' - ' + endIndex + ' ' + $localize`:@@of:of` + ' ' + length;
    };

    return paginatorIntl;
}
